import { ReactNode } from "react";
import { Outlet, useParams, useSearchParams } from "react-router-dom";

import { LoadingSpinner } from "../../ui/loadingSpinner/loadingSpinner";
import { useOptroViewAccess } from "../../../hooks/useOptroViewAccess";
import { OPTRO_ACCESS_CHECKING_LABEL } from "../../../lib/optroAccessMessages";
import { OptroAccessKind } from "../../../models/audit/optroAccess";
import { OptroAccessErrorPage } from "./OptroAccessErrorPage";

type RequireOptroViewAccessProps = {
  kind: OptroAccessKind;
  // Rendered once allowed; without children the guard is a layout route and
  // renders its `<Outlet />`.
  children?: ReactNode;
} & (
  | {
      // The route param holding the id. Read with `useParams`, which also
      // sees a child route's params, so a layout can guard by its children's
      // id.
      idParam: string;
      idSearchParam?: never;
    }
  | {
      // The query-string param holding the id, for a page that names its
      // entity there (`collab/refine?mode=refine&id=…`).
      idSearchParam: string;
      idParam?: never;
    }
);

/**
 * Renders the guarded page only once Optro view access is confirmed. Until
 * then nothing of the page mounts — not its data hooks, not its providers —
 * so neither a request nor the entity's name gets out ahead of the answer.
 * With `respect-optro-permissions` off, or not yet known to be on, it renders
 * the page as is, without asking. Once the page has rendered for an id, a
 * later re-check of that id (flags resolving to on, or the flag turning on
 * mid-session) keeps it mounted unless the answer is a definitive refusal —
 * see `useOptroViewAccess`.
 */
export const RequireOptroViewAccess = ({
  kind,
  idParam,
  idSearchParam,
  children,
}: RequireOptroViewAccessProps) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id =
    idParam !== undefined
      ? params[idParam]
      : idSearchParam !== undefined
        ? searchParams.get(idSearchParam)
        : undefined;
  const { state, retry } = useOptroViewAccess(kind, id);

  if (state === "checking") {
    return (
      <div className="flex h-full min-h-screen items-center justify-center">
        <LoadingSpinner label={OPTRO_ACCESS_CHECKING_LABEL} />
      </div>
    );
  }
  if (state !== "allowed") {
    return <OptroAccessErrorPage kind={kind} failure={state} onRetry={retry} />;
  }
  return <>{children ?? <Outlet />}</>;
};
